import React, { useContext, useState, useEffect } from 'react';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../scaffold/UserContext';
import { DesktopMenu } from './desktop-menu/DesktopMenu';
import { getNavigation } from '../../../client/fetch/get-navigation';
import {
  addMediaQueryListener,
  isTabletPortrait,
} from '../../utils/device/identifyDevice';
import { SEOLinks } from './seo-links/SEOLinks';
import { MobileMenu } from './mobile-menu/MobileMenu/MobileMenu';
import { MOBILE_MI } from '../../utils/constants';

type NavigationProps = {
  mobileNavShown: boolean;
  typeaheadIsOpen?: boolean;
};

export const fetchChildCategories =
  (queryParams?: string, subset?: string, exp?: string) =>
  ({ id }: { id: string }) =>
    getNavigation({
      id,
      depth: 4,
      subset,
      queryParams,
      ...(exp ? { exp } : {}),
    });

export const Navigation = ({
  mobileNavShown,
  typeaheadIsOpen = false,
}: NavigationProps) => {
  const {
    categories,
    envVars: { monetate },
    features: { monetateExperiences, displaySEOLinks },
    headerClientConfigQueryParameters,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);
  const [flyoutCategories, setFlyoutCategories] = useState<{
    child?: MenuItem[];
  }>({});
  const [shouldRenderMobileMenu, setShouldRenderMobileMenu] = useState(false);

  const subset = `MOBILE_WEB,FLYOUT`;

  const exp =
    (typeof window !== 'undefined' && window?.__PRELOADED_STATE__?.exp) || '';

  const fetch = fetchChildCategories(
    headerClientConfigQueryParameters,
    subset,
    exp
  );

  const fetchFlyoutCategories = async () => {
    const navCategories = await getNavigation({
      subset: 'MOBILE_WEB,FLYOUT',
      queryParams: headerClientConfigQueryParameters,
    });
    if (navCategories && 'child' in navCategories) {
      setFlyoutCategories(navCategories as { child?: MenuItem[] });
    }
  };

  useEffect(function listenForFlyoutFetchBreakpoint() {
    if (
      typeof window !== 'undefined' &&
      isTabletPortrait() &&
      !flyoutCategories?.child
    ) {
      const cleanup = addMediaQueryListener(fetchFlyoutCategories);
      return cleanup; // Clean up on component unmount
    }
    return function noopFlyoutFetchCleanup() {}; // Empty cleanup if "if" conditions not met
  }, []);

  useEffect(
    function recordMobileMenuImpression() {
      if (mobileNavShown) {
        recordImpressionByName(
          monetate?.channel,
          monetateExperiences as unknown as Parameters<
            typeof recordImpressionByName
          >[1],
          MOBILE_MI,
          monetateId
        );
      }
    },
    [mobileNavShown]
  );

  // Handle immediate showing and delayed hiding for animation
  useEffect(
    function showMobileMenuOnOpen() {
      if (mobileNavShown) {
        setShouldRenderMobileMenu(true);
      }
    },
    [mobileNavShown]
  );

  const handleAnimationComplete = () => {
    if (!mobileNavShown) {
      setShouldRenderMobileMenu(false);
    }
  };

  if (shouldRenderMobileMenu) {
    return (
      <MobileMenu
        data={categories.child}
        fetchChildCategories={fetch}
        typeaheadIsOpen={typeaheadIsOpen}
        isVisible={mobileNavShown}
        onAnimationComplete={handleAnimationComplete}
      />
    );
  }

  return (
    <>
      <DesktopMenu
        data={
          flyoutCategories.child ? flyoutCategories.child : categories.child
        }
      />
      {displaySEOLinks && <SEOLinks categories={categories.child} />}
    </>
  );
};
